import { Component, signal, ChangeDetectionStrategy, inject, computed, effect, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { EventRegisterStorageService } from 'src/app/module/events/pages/event-main/event-register/services/event-register-storage.service';

import { EventsListComponent } from 'src/app/module/events/pages/events-list/events-list.component';
import { HomeBannerComponent, HomeBannerSlide } from './components/home-banner/home-banner.component';
import { HomeBenefitsComponent } from './components/home-benefits/home-benefits.component';
import { HomeCtaComponent } from './components/home-cta/home-cta.component';
import { HowItWorksModalComponent } from './components/how-it-works-modal/how-it-works-modal.component';

interface Benefit {
  id: number;
  icon: string;
  title: string;
  description: string;
  badge?: string;
  delay: string;
  color: string;
  gradient: string;
}

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    HomeBannerComponent,
    EventsListComponent,
    HomeBenefitsComponent,
    HomeCtaComponent,
    HowItWorksModalComponent
]
})
export class HomeComponent implements OnInit {
  private router = inject(Router);
  private readonly eventRegisterStorageService = inject(EventRegisterStorageService);

  protected readonly showHowItWorksModal = signal(false);
  protected readonly authService = inject(AuthService);
  private readonly title = inject(Title);
  private readonly platformId = inject(PLATFORM_ID);
  
  // Computed signal para determinar se deve mostrar conteúdo promocional
  protected readonly shouldShowPromo = computed(() => {
    const isLogged = this.authService.isLogged();
    // Só mostra para usuários NÃO logados
    return !isLogged;
  });

  public readonly bannerSlides: readonly HomeBannerSlide[] = [
    {
      id: 'explorar-eventos',
      kicker: 'Compra 100% segura',
      kickerIcon: 'pi-shield',
      title: 'Seu próximo evento começa aqui',
      subtitle: 'Ingressos para shows, festas, teatros e cursos com pagamento por PIX ou cartão.',
      ctaLabel: 'Explorar eventos',
      accent: '#F2994A',
      image: 'assets/banners/banner22.png',
      imageAlt: 'Duas amigas animadas em uma plateia de show com o palco iluminado ao fundo',
      imagePosition: 'center 38%'
    },
    {
      id: 'criar-evento',
      kicker: 'Para organizadores',
      kickerIcon: 'pi-megaphone',
      title: 'Crie e venda seu evento na plataforma',
      subtitle: 'Publique em minutos e acompanhe as vendas em tempo real, com check-in por QR Code.',
      ctaLabel: 'Criar meu evento',
      ctaLink: '/auth/registro/criador-de-evento',
      accent: '#16ADDE',
      image: 'assets/banners/banner33.png',
      imageAlt: 'Duas amigas sorrindo em um show, uma delas mostrando o ingresso no celular',
      imagePosition: 'center 8%'
    },
    {
      id: 'how-it-works',
      kicker: 'Sem filas',
      kickerIcon: 'pi-qrcode',
      title: 'Ingresso digital direto no seu celular',
      subtitle: 'Receba por e-mail e entre no evento apresentando o QR Code. Simples assim.',
      ctaLabel: 'Como funciona',
      accent: '#8B5CF6',
      image: 'assets/banners/banner11.png',
      imageAlt: 'Mulher sorrindo mostrando o ingresso digital com QR Code no celular em um show',
      imagePosition: 'center 23%'
    }
  ] as const;
  
  public readonly benefits: readonly Benefit[] = [
    {
      id: 1,
      icon: 'pi pi-ticket',
      title: 'Venda de ingressos online',
      description: 'Plataforma completa para vender seus ingressos 24/7.',
      badge: 'Popular',
      delay: '0s',
      color: '#F2994A',
      gradient: 'linear-gradient(135deg, #F2994A 0%, #F27121 100%)'
    },
    {
      id: 2,
      icon: 'pi pi-credit-card',
      title: 'Pagamentos seguros',
      description: 'PIX e cartão com total segurança.',
      delay: '0.1s',
      color: '#10B981',
      gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
    },
    {
      id: 3,
      icon: 'pi pi-chart-bar',
      title: 'Relatórios em tempo real',
      description: 'Acompanhe suas vendas de qualquer lugar.',
      delay: '0.2s',
      color: '#3B82F6',
      gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
    },
    {
      id: 4,
      icon: 'pi pi-qrcode',
      title: 'Check-in digital',
      description: 'Validação rápida com QR Code no evento.',
      badge: 'Novo',
      delay: '0.3s',
      color: '#8B5CF6',
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'
    },
    {
      id: 5,
      icon: 'pi pi-mobile',
      title: 'Ingressos digitais',
      description: 'Envio automático por e-mail e WhatsApp.',
      delay: '0.4s',
      color: '#EC4899',
      gradient: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)'
    }
  ] as const;

  constructor() {
    // Effect para monitorar mudanças no estado de login
    effect(() => {
      this.authService.isLogged();
    });
  }

  ngOnInit(): void {
    // Resetar título para o padrão quando a home é carregada
    this.title.setTitle('Seu Ingresso Aqui - Compre Ingressos para Eventos, Shows e Teatros Online');
  }

  protected openHowItWorksModal(): void {
    this.showHowItWorksModal.set(true);
  }

  protected onBannerCta(slide: HomeBannerSlide): void {
    if (slide.id === 'how-it-works') {
      this.openHowItWorksModal();
      return;
    }
    if (slide.id === 'explorar-eventos') {
      this.scrollToEventsList();
      return;
    }
    if (slide.id === 'criar-evento') {
      this.navigateToCreateEvent();
      return;
    }
    if (slide.ctaLink) {
      this.router.navigateByUrl(slide.ctaLink);
    }
  }

  private scrollToEventsList(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    document.getElementById('home-events-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  protected navigateToCreateEvent(): void {
    const canCreateEvent = this.authService.isProducer() || this.authService.isAdmin();
    const destination = canCreateEvent
      ? '/eventos/registrar/sobre'
      : '/auth/registro/criador-de-evento';

    if (canCreateEvent) {
      this.eventRegisterStorageService.deleteEventRegisterInfo();
    }

    this.router.navigateByUrl(destination);
  }
}
